import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Loader2, AlertTriangle, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function SlotList({ slots, picked, setPicked, staff }) {
  return (
    <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="reschedule-slots">
      {slots.map((s) => (
        <label key={s.offering_id} data-testid={`reschedule-slot-${s.offering_id}`}
          className={`flex items-center justify-between gap-3 p-2.5 border cursor-pointer transition-colors ${picked === s.offering_id ? "border-[#CCFF00] bg-[#CCFF00]/5" : "border-[#262626] hover:border-[#404040]"}`}>
          <span className="flex items-center gap-2 text-sm">
            <input type="radio" name="reschedule-slot" checked={picked === s.offering_id} onChange={() => setPicked(s.offering_id)} className="accent-[#CCFF00]" />
            {fmt(s.start)} · {s.duration_min} min{s.venue ? ` · ${s.venue}` : ""}
          </span>
          {s.price_difference > 0 && (
            <span className="text-[11px] uppercase tracking-wider text-[#FF9F1C]">
              {staff ? `you absorb £${s.price_difference}` : `+£${s.price_difference} to pay`}
            </span>
          )}
        </label>
      ))}
    </div>
  );
}

export function RescheduleSession({ bookingId, staff = false, onDone, testid = "reschedule" }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [opts, setOpts] = useState(null);
  const [picked, setPicked] = useState("");
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get(`/bookings/${bookingId}/reschedule-options`, { params: { show_all: showAll } })
      .then((r) => setOpts(r.data)).catch(() => setOpts({ reschedulable: false }));
  }, [open, showAll, bookingId]);

  const move = async () => {
    setBusy(true);
    try {
      const body = picked ? { target_offering_id: picked } : { new_start: custom };
      const { data } = await api.post(`/bookings/${bookingId}/reschedule-move`, body);
      if (data.amount_due > 0) { toast.message(`Rescheduled — £${data.amount_due} difference to pay`); nav(`/pay/booking/${data.booking_id}`); return; }
      toast.success(staff ? "Session rescheduled — the player's payment carried over" : "Session rescheduled — your original payment carried over");
      setOpen(false);
      onDone?.(data.booking_id);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't reschedule"); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} data-testid={testid}
        className="min-h-[44px] px-4 py-2 text-xs uppercase tracking-wider border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00] active:scale-95 transition-colors inline-flex items-center gap-1.5">
        <CalendarClock className="w-3.5 h-3.5" /> Reschedule
      </button>
    );
  }
  const close = <button type="button" onClick={() => setOpen(false)} className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white" data-testid={`${testid}-close`}>Close</button>;
  if (!opts) return <div className="text-sm text-[#A3A3A3] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Finding sessions…</div>;
  if (!opts.reschedulable) {
    return <div className="text-sm text-[#A3A3A3] space-x-3" data-testid={`${testid}-not-allowed`}><span>This session can't be rescheduled — cancel and re-book instead.</span>{close}</div>;
  }
  if (!staff && opts.late) {
    return (
      <div className="text-xs text-[#FF9F1C] inline-flex items-start gap-2 max-w-lg" data-testid={`${testid}-late-warning`}>
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>This is inside the {opts.cutoff_hours}-hour cut-off, so it can no longer be rescheduled. Contact your coach if something has come up. {close}</span>
      </div>
    );
  }
  const none = opts.slots.length === 0;
  return (
    <div className="card-surface p-4 space-y-3 w-full max-w-xl" data-testid={`${testid}-panel`}>
      <div className="label-mono">PICK A NEW TIME</div>
      {none && !staff ? (
        <div className="space-y-3" data-testid={`${testid}-empty`}>
          <div className="text-sm">No sessions available right now.</div>
          <Link to="/request/new" data-testid={`${testid}-request`}
            className="min-h-[44px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-2">
            <Send className="w-3.5 h-3.5" /> Request a session
          </Link>
        </div>
      ) : none ? (
        <div className="text-sm text-[#A3A3A3]" data-testid={`${testid}-empty`}>No open 1:1 slots — set a custom time below.</div>
      ) : (
        <SlotList slots={opts.slots} picked={picked} setPicked={(v) => { setPicked(v); setCustom(""); }} staff={staff} />
      )}
      {!staff && (opts.hidden_pricier > 0 || showAll) && (
        <label className="flex items-center gap-2 text-xs text-[#A3A3A3]" data-testid={`${testid}-show-all`}>
          <input type="checkbox" checked={showAll} onChange={(e) => { setShowAll(e.target.checked); setPicked(""); }} className="accent-[#CCFF00]" />
          Show pricier sessions too (you pay the difference){!showAll ? ` · ${opts.hidden_pricier} more` : ""}
        </label>
      )}
      {staff && opts.can_custom_time && (
        <label className="block">
          <span className="label-mono text-[#A3A3A3]">Or set a custom date &amp; time</span>
          <input type="datetime-local" value={custom} onChange={(e) => { setCustom(e.target.value); setPicked(""); }}
            data-testid={`${testid}-custom`}
            className="mt-1 w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none p-2 text-sm" />
        </label>
      )}
      {!(none && !staff) && <div className="text-xs text-[#737373]">The credit or payment already made carries over — no new charge.</div>}
      <div className="flex items-center gap-3">
        {!(none && !staff) && (
          <button type="button" onClick={move} disabled={busy || (!picked && !custom)} data-testid={`${testid}-confirm`}
            className="min-h-[40px] px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black inline-flex items-center gap-1.5 disabled:opacity-40">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Confirm new time
          </button>
        )}
        {close}
      </div>
    </div>
  );
}
